import React from "react";
import { useParams, Navigate } from "react-router-dom";
import SeoPage from "../../components/SeoPage";
import { PRESETS } from "../../lib/exportPresets";

// Single page component that renders a destination-specific SEO landing page
// for every preset. The URL pattern is /save-csv-for-:preset (where :preset
// is the preset id) — e.g. /save-csv-for-asana, /save-csv-for-excel-windows.
//
// One file, N landing pages — each one a high-intent capture for queries like
// "asana csv import not working" or "excel csv shows wrong characters".

export default function SaveCsvForPreset() {
    const { preset: presetId } = useParams();
    const preset = PRESETS.find((p) => p.id === presetId);
    if (!preset) return <Navigate to="/import-export-csv" replace />;

    const destName = preset.label
        .replace(/^Save for /, "")
        .replace(/ import$/, "")
        .replace(/ audience$/, "");
    const verb = preset.id === "tsv" || preset.id === "plain" ? "export" : "import to";

    return (
        <SeoPage
            title={`${preset.label} — Export CSV for ${destName} | OpenCSV`}
            description={`${preset.tagline} Open your CSV in OpenCSV, pick "${preset.label}", and download a file ${destName} accepts on the first try.`}
            breadcrumbs={[
                { label: "Home", href: "/" },
                { label: "Import / Export", href: "/import-export-csv" },
                { label: `Save for ${destName}` },
            ]}
            eyebrow="/ export preset"
            h1={`Save your CSV for ${destName}.`}
            heroLead={preset.tagline + " No more guessing at delimiters, encodings, or line endings — pick the destination, get a file that works."}
            heroBullets={[
                preset.bom ? "UTF-8 with BOM" : "UTF-8 without BOM",
                preset.newline === "\r\n" ? "CRLF (Windows-style) line endings" : "LF (Mac/Linux) line endings",
                preset.delimiter === "\t" ? "Tab-separated" : preset.delimiter === ";" ? "Semicolon-separated" : "Comma-separated",
                preset.quotes === "all" ? "Every field quoted (Salesforce-strict)" : preset.quotes === false ? "No quoting" : "Smart quoting (only when needed)",
            ]}
            sections={[
                {
                    heading: `Why ${destName} needs a specific CSV format`,
                    body: preset.notes,
                },
                {
                    heading: "How to use the preset",
                    body: [
                        "1. Open OpenCSV at opencsv.io/app and drag your CSV file onto the editor.",
                        "2. Make any edits you need — paste rows from Excel, fix column names, delete empty rows.",
                        `3. Click the Export button → pick "${preset.label}".`,
                        `4. You'll get a download named like \`yourfile${preset.filenameSuffix || ""}.${preset.ext}\`.`,
                        `5. Upload that file to ${destName}. It will import on the first try.`,
                    ],
                },
                {
                    heading: "Why this preset exists",
                    body: `Different platforms expect CSVs in subtly different formats. ${destName} is one of those tools where the wrong combination of byte-order mark, line endings, or quote style breaks the import silently. Instead of debugging that yourself, let OpenCSV produce the exact format ${destName} expects.`,
                },
            ]}
            faqs={[
                {
                    q: `Why doesn't my CSV import into ${destName}?`,
                    a: `Most ${destName} import failures come from one of three things: a UTF-8 byte-order mark that the importer chokes on, line endings that don't match what ${destName} expects, or quoting rules that escape characters wrong. The "${preset.label}" preset in OpenCSV produces a file with all three correct for ${destName}.`,
                },
                {
                    q: "Does this change my data?",
                    a: "No. Your rows, columns, headers, and cell contents stay identical. Only the file's metadata (encoding marker, line break characters, quote rules) is adjusted to match what the target tool expects.",
                },
                {
                    q: "Is OpenCSV free?",
                    a: "OpenCSV is free to open and view. Editing, cloud save, and the Export presets are part of OpenCSV Pro ($9.99/month or $79/year, 14-day free trial, cancel anytime).",
                },
            ]}
            related={[
                { href: "/import-export-csv", label: "All CSV import/export topics" },
                { href: "/what-is-a-csv-file", label: "What is a CSV file?" },
                { href: "/csv-vs-excel", label: "CSV vs Excel — what's the difference?" },
                { href: "/app", label: `Open OpenCSV and ${verb} ${destName}` },
            ]}
            ctaTitle={`Stop fighting ${destName}'s import.`}
            ctaSub={`Open your CSV in OpenCSV, hit "${preset.label}", and re-upload. Works first try. 14-day Pro trial, $9.99/mo.`}
        />
    );
}
